/**
 * AppToaster — the app-wide toast host.
 *
 * Wraps the tree in the DS `Toast.Provider` and renders a single `Toast.Viewport`
 * (portalled to `<body>`) that maps the manager's live toasts onto DS toast parts.
 * Any component below can then raise a toast with the DS `useToastManager()` hook:
 *
 *   const toast = useToastManager();
 *   toast.add({ title: "Correction saved", description: "…" });
 *
 * DS-only: every part comes from `@norican/react`; nothing is hand-rolled.
 *
 * Where the stack sits (`toastPositionOf`): bottom right on a laptop. On a narrow screen
 * (a phone, the Outlook pane) it sits at the top: at the bottom it covered the big button
 * and "More" of the pinned action bar for about 6 s after every save (review of the ease
 * round, 13 Oct 2026).
 */

import { Toast, type ToastPosition, useToastManager } from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";

/** The corner of the toast stack: away from the pinned action bar on a narrow screen. */
export function toastPositionOf(narrow: boolean): ToastPosition {
  return narrow ? "top-center" : "bottom-right";
}

/** Renders the live toast stack — one DS `Toast.Root` per queued toast. */
function ToastList() {
  const { toasts } = useToastManager();
  return (
    <>
      {toasts.map((toast) => (
        <Toast.Root key={toast.id} toast={toast}>
          <Toast.Content>
            <Toast.Title />
            <Toast.Description />
            {/* The button of a toast raised with `actionProps` ("Undo" after No item);
                the DS part renders nothing without them. */}
            <Toast.Action />
          </Toast.Content>
          <Toast.CloseButton />
        </Toast.Root>
      ))}
    </>
  );
}

/** Provides the toast context and mounts the viewport once, around the whole app. */
export function AppToaster({ children }: { children: ReactNode }) {
  const narrow = useMediaQuery(NARROW_QUERY);
  return (
    <Toast.Provider>
      {children}
      <Toast.Portal>
        <Toast.Viewport position={toastPositionOf(narrow)}>
          <ToastList />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>
  );
}
